import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { MapPin, Maximize2, Heart, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useCurrency } from "@/contexts/CurrencyContext";
import { isRTL } from "@/i18n";

const filterKeys = [
  { labelKey: "filters.all", value: "all" },
  { labelKey: "filters.sale", value: "satilik" },
  { labelKey: "filters.rent", value: "kiralik" },
  { labelKey: "filters.villa", value: "villa" },
  { labelKey: "filters.apartment", value: "apartman" },
  { labelKey: "filters.land", value: "arsa" },
];

// Fallback statik veriler (Supabase başarısız olursa kullanılır)
const fallbackProperties = [
  { id: 1, titleKey: "1", priceGBP: 385000, sqm: "240 m²", type: "villa", status: "satilik", bedrooms: 4, image: "https://images.pexels.com/photos/29453302/pexels-photo-29453302.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", featured: true, rental: false },
  { id: 2, titleKey: "2", priceGBP: 195000, sqm: "120 m²", type: "apartman", status: "satilik", bedrooms: 2, image: "https://images.pexels.com/photos/29334668/pexels-photo-29334668.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", featured: false, rental: false },
  { id: 3, titleKey: "3", priceGBP: 850, sqm: "95 m²", type: "apartman", status: "kiralik", bedrooms: 3, image: "https://images.pexels.com/photos/6970049/pexels-photo-6970049.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", featured: false, rental: true },
  { id: 4, titleKey: "4", priceGBP: 520000, sqm: "180 m²", type: "apartman", status: "satilik", bedrooms: 3, image: "https://images.pexels.com/photos/7546314/pexels-photo-7546314.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", featured: true, rental: false },
  { id: 5, titleKey: "5", priceGBP: 125000, sqm: "800 m²", type: "arsa", status: "satilik", bedrooms: 0, image: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=940&h=650&fit=crop", featured: false, rental: false },
  { id: 6, titleKey: "6", priceGBP: 1200, sqm: "200 m²", type: "villa", status: "kiralik", bedrooms: 4, image: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=940&h=650&fit=crop", featured: false, rental: true },
];

export default function ListingGrid({ properties: externalProperties, loading: externalLoading = false }) {
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const [activeFilter, setActiveFilter] = useState("all");
  const [visibleCards, setVisibleCards] = useState(new Set());
  const cardRefs = useRef([]);
  const rtl = isRTL(i18n.language);

  // Eğer dışarıdan properties gelmişse kullan, yoksa fallback kullan
  const properties = externalProperties || fallbackProperties;
  const loading = externalLoading;

  const filtered = activeFilter === "all"
    ? properties
    : properties.filter((p) => p.type === activeFilter || p.status === activeFilter);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setVisibleCards((prev) => new Set([...prev, entry.target.dataset.cardId]));
        });
      },
      { threshold: 0.15 }
    );
    cardRefs.current.forEach((ref) => { if (ref) observer.observe(ref); });
    return () => observer.disconnect();
  }, [filtered]);

  return (
    <section id="listings" data-testid="listings-section" className="py-20 md:py-32 px-6 md:px-12 bg-[#F8F9FA]" dir={rtl ? "rtl" : "ltr"}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="gold-line" />
            <span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t("listings.pretitle")}
            </span>
            <div className="gold-line" />
          </div>
          <h2 data-testid="listings-title" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A192F] tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("listings.title")}
          </h2>
          <p className="text-[#4A5568] text-base max-w-2xl mx-auto" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {t("listings.subtitle")}
          </p>
        </div>

        <div data-testid="listing-filters" className="flex flex-wrap items-center justify-center gap-2 mb-12">
          {filterKeys.map((f) => (
            <button
              key={f.value}
              data-testid={`filter-${f.value}`}
              onClick={() => setActiveFilter(f.value)}
              className={`px-5 py-2.5 text-sm font-medium rounded-sm transition-all duration-300 ${
                activeFilter === f.value
                  ? "bg-[#0A192F] text-white shadow-lg"
                  : "bg-white text-[#4A5568] hover:text-[#0A192F] border border-[#E2E8F0] hover:border-[#D4AF37]"
              }`}
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {t(`listings.${f.labelKey}`)}
            </button>
          ))}
        </div>

        {/* Loading state */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white border border-[#E2E8F0] rounded-sm overflow-hidden animate-pulse">
                <div className="h-64 bg-gray-200" />
                <div className="p-6 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-1/3" />
                  <div className="h-6 bg-gray-200 rounded w-3/4" />
                  <div className="h-5 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((property, i) => {
              // Supabase'den gelen veri mi, statik veri mi kontrol et
              const isFromSupabase = property.title !== undefined && !property.titleKey;
              const displayImage = property.image || (property.images?.[0]) || '';
              const displayTitle = isFromSupabase ? property.title : t(`listings.properties.${property.titleKey}.title`);
              const displayLocation = isFromSupabase ? property.location : t(`listings.properties.${property.titleKey}.location`);
              const displaySqm = property.sqm || (property.sqm ? `${property.sqm} m²` : '');

              return (
                <Link
                  to={`/${i18n.language}/properties/${property.id}`}
                  key={property.id}
                  ref={(el) => (cardRefs.current[i] = el)}
                  data-card-id={property.id}
                  data-testid={`property-card-${property.id}`}
                  className={`bg-white border border-[#E2E8F0] rounded-sm overflow-hidden group hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 ${
                    visibleCards.has(String(property.id)) ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                  }`}
                  style={{ transitionDelay: `${i * 0.1}s` }}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img src={displayImage} alt={displayTitle} className="w-full h-full object-cover card-img-zoom" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className={`absolute top-4 ${rtl ? "right-4" : "left-4"} flex gap-2`}>
                      <Badge className="bg-[#0A192F] text-white text-[10px] uppercase tracking-wider border-0 rounded-sm px-3 py-1">
                        {property.status === "satilik" ? t("listings.badge_sale") : t("listings.badge_rent")}
                      </Badge>
                      {property.featured && (
                        <Badge className="bg-[#D4AF37] text-white text-[10px] uppercase tracking-wider border-0 rounded-sm px-3 py-1">
                          {t("listings.badge_featured")}
                        </Badge>
                      )}
                    </div>
                    <button
                      data-testid={`favorite-${property.id}`}
                      className={`absolute top-4 ${rtl ? "left-4" : "right-4"} w-9 h-9 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white hover:scale-110`}
                    >
                      <Heart className="w-4 h-4 text-[#0A192F]" />
                    </button>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-1.5 mb-2">
                      <MapPin className="w-3.5 h-3.5 text-[#D4AF37]" />
                      <span className="text-xs text-[#4A5568]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {displayLocation}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-[#0A192F] mb-3 group-hover:text-[#D4AF37] transition-colors duration-300" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {displayTitle}
                    </h3>

                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center gap-1.5">
                        <Maximize2 className="w-3.5 h-3.5 text-[#4A5568]" />
                        <span className="text-xs text-[#4A5568]" style={{ fontFamily: "'Outfit', sans-serif" }}>{displaySqm}</span>
                      </div>
                      {property.bedrooms > 0 && (
                        <div className="flex items-center gap-1.5">
                          <svg className="w-3.5 h-3.5 text-[#4A5568]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 7v11a1 1 0 001 1h16a1 1 0 001-1V7" />
                            <path d="M21 11H3V7a2 2 0 012-2h14a2 2 0 012 2v4z" />
                          </svg>
                          <span className="text-xs text-[#4A5568]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                            {property.bedrooms} {t("listings.bedrooms")}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-[#E2E8F0]">
                      <span className="text-xl font-bold text-[#0A192F]" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {formatPrice(property.priceGBP, i18n.language)}{property.rental ? t("listings.per_month") : ""}
                      </span>
                      <span
                        data-testid={`view-property-${property.id}`}
                        className="flex items-center gap-1.5 text-[#D4AF37] text-sm font-medium group-hover:gap-3 transition-all duration-300"
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                      >
                        {t("listings.detail")}
                        <ArrowRight className={`w-4 h-4 ${rtl ? "rotate-180" : ""}`} />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        <div className="text-center mt-14">
          <button
            data-testid="view-all-listings"
            className="inline-flex items-center gap-2 px-8 py-3.5 border-2 border-[#0A192F] text-[#0A192F] text-sm font-medium rounded-sm hover:bg-[#0A192F] hover:text-white transition-all duration-300"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t("listings.view_all")}
            <ArrowRight className={`w-4 h-4 ${rtl ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>
    </section>
  );
}
